import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/nav-section.default.js';
import * as wireframeSkin from './skins/nav-section.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

class RunNavSection extends RunElement {
  #open = false;
  #level = 0;
  #disabled = false;
  #label = '';
  #rendering = false;
  // Tracks whether the one-shot defaultOpen has already been consumed so that
  // reconnect does not re-apply it.
  #defaultOpenConsumed = false;
  // Set to true in keydown handler when focus should return to trigger after
  // the upcoming re-render completes (skin patches in place so same element).
  #pendingFocusTrigger = false;

  static get observedAttributes() {
    return ['open', 'disabled', 'label', 'level'];
  }

  _onTriggerClickBound = (e) => this.#handleTriggerClick(e);
  _onTriggerKeydownBound = (e) => this.#handleTriggerKeydown(e);
  _onSlotChangeBound = (e) => this.#handleSlotChange(e);

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'navigation');
    // defaultOpen one-shot: read attribute only on first connect, never on reconnect.
    if (!this.#defaultOpenConsumed && this.hasAttribute('default-open')) {
      this.#defaultOpenConsumed = true;
      this.removeAttribute('default-open');
      // Set directly without emitting an event during connect
      this.#open = true;
      this.toggleAttribute('open', true);
    }
    // Mirror level to data-level on connect so the attribute is always present.
    const levelStr = String(this.#level);
    if (this.getAttribute('data-level') !== levelStr) {
      this.setAttribute('data-level', levelStr);
    }
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#detachTriggerListeners();
    if (this.shadowRoot) {
      this.shadowRoot.querySelectorAll('slot').forEach(slot => {
        slot.removeEventListener('slotchange', this._onSlotChangeBound);
      });
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'open':     this.open     = newValue !== null; break;
      case 'disabled': this.disabled = newValue !== null; break;
      case 'label':    this.label    = newValue !== null ? String(newValue).trim() : ''; break;
      case 'level':    this.level    = newValue; break;
    }
  }

  // ── Properties ────────────────────────────────────────────────────────────

  get open() { return this.#open; }
  set open(value) {
    const coerced = Boolean(value);
    if (this.#open === coerced) return;
    this.#open = coerced;
    this.toggleAttribute('open', coerced);
    this.#emitToggle();
    this.requestUpdate();
  }

  get level() { return this.#level; }
  set level(value) {
    const n = parseInt(value, 10);
    // NaN → 0, negative → 0, otherwise use as-is
    const coerced = Number.isFinite(n) && n >= 0 ? n : 0;
    if (this.#level === coerced) return;
    this.#level = coerced;
    const str = String(coerced);
    if (this.getAttribute('level') !== str) this.setAttribute('level', str);
    if (this.getAttribute('data-level') !== str) this.setAttribute('data-level', str);
    this.requestUpdate();
  }

  get disabled() { return this.#disabled; }
  set disabled(value) {
    const coerced = Boolean(value);
    if (this.#disabled === coerced) return;
    this.#disabled = coerced;
    this.toggleAttribute('disabled', coerced);
    this.#propagateDisabled(coerced);
    this.requestUpdate();
  }

  get label() { return this.#label; }
  set label(value) {
    const coerced = String(value == null ? '' : value).trim();
    if (this.#label === coerced) return;
    this.#label = coerced;
    if (this.getAttribute('label') !== coerced) this.setAttribute('label', coerced);
    this.requestUpdate();
  }

  // Alias used by skin modules that were authored against the old `expanded` name.
  get expanded() { return this.#open; }

  // Stable region ID generated once per instance; skin reads this for aria-controls.
  get _regionId() {
    if (!this.__regionId) {
      this.__regionId = 'nav-section-' + Math.random().toString(36).slice(2, 8);
    }
    return this.__regionId;
  }

  // ── Public methods ────────────────────────────────────────────────────────

  toggle() {
    if (this.#disabled) return;
    this.open = !this.#open;
  }

  expand() {
    if (this.#disabled || this.#open) return;
    this.open = true;
  }

  collapse() {
    if (this.#disabled || !this.#open) return;
    this.open = false;
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
      // Sync region visibility and measure height for CSS animation.
      this.#syncRegion();
      // Re-attach trigger listeners (first render creates the trigger; subsequent
      // renders patch in place so the reference is stable).
      this.#attachTriggerListeners();
      // Return focus to trigger when a keyboard close gesture requested it.
      if (this.#pendingFocusTrigger) {
        this.#pendingFocusTrigger = false;
        const trigger = this.shadowRoot.querySelector('.trigger');
        if (trigger) trigger.focus();
      }
      this.shadowRoot.querySelectorAll('slot').forEach(slot => {
        slot.removeEventListener('slotchange', this._onSlotChangeBound);
        slot.addEventListener('slotchange', this._onSlotChangeBound);
      });
    } finally {
      this.#rendering = false;
    }
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  #syncRegion() {
    const region = this.shadowRoot && this.shadowRoot.querySelector('[part="region"]');
    if (!region) return;
    if (this.#open) {
      region.removeAttribute('hidden');
      // Measure scrollHeight for the CSS animation token. jsdom returns 0; real
      // browsers return the natural height. Either way, set the property so tests
      // can verify it was written.
      const h = region.scrollHeight ? `${region.scrollHeight}px` : '0px';
      this.style.setProperty('--run-nav-section-measured-height', h);
    } else {
      region.setAttribute('hidden', '');
    }
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  #attachTriggerListeners() {
    const trigger = this.shadowRoot && this.shadowRoot.querySelector('.trigger');
    if (!trigger || trigger === this.__trigger) return;
    this.#detachTriggerListeners();
    this.__trigger = trigger;
    trigger.addEventListener('click', this._onTriggerClickBound);
    trigger.addEventListener('keydown', this._onTriggerKeydownBound);
  }

  #detachTriggerListeners() {
    if (!this.__trigger) return;
    this.__trigger.removeEventListener('click', this._onTriggerClickBound);
    this.__trigger.removeEventListener('keydown', this._onTriggerKeydownBound);
    this.__trigger = null;
  }

  #handleTriggerClick() {
    if (this.#disabled) return;
    this.open = !this.#open;
  }

  #handleTriggerKeydown(event) {
    if (this.#disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const wasOpen = this.#open;
      // Decide whether to return focus before the state change, while we still
      // know where focus is.  The actual focus() call happens in updated() so
      // it lands on the (patched-in-place) trigger AFTER the re-render.
      const shouldReturnFocus = wasOpen &&
        (this.contains(document.activeElement) || this.shadowRoot.contains(document.activeElement));
      this.open = !this.#open;
      if (shouldReturnFocus && !this.#open) {
        this.#pendingFocusTrigger = true;
      }
    }
  }

  #handleSlotChange(event) {
    const slot = event.target;
    const slotName = slot.getAttribute('name');
    const assigned = slot.assignedElements({ flatten: true });
    assigned.forEach(el => {
      if ('disabled' in el) el.disabled = this.#disabled;
    });
    if (slotName) {
      this.toggleAttribute(`data-has-${slotName}`, assigned.length > 0);
    }
  }

  #propagateDisabled(value) {
    if (!this.shadowRoot) return;
    this.shadowRoot.querySelectorAll('slot').forEach(slot => {
      slot.assignedElements({ flatten: true }).forEach(el => {
        if ('disabled' in el) el.disabled = value;
      });
    });
  }

  #emitToggle() {
    this.dispatchEvent(new CustomEvent('run-nav-section-toggle', {
      bubbles: true,
      composed: true,
      detail: { open: this.#open, level: this.#level },
    }));
  }
}

if (!customElements.get('run-nav-section')) {
  customElements.define('run-nav-section', RunNavSection);
}

export { RunNavSection };
export default RunNavSection;
